<template>
  <div class="usage-hierarchy-picker" :style="{ '--picker-row-count': pickerRowCount }" @click.stop>
    <div class="picker-search picker-search-full">
      <IconSearch class="h-4 w-4 text-neutral-550" stroke="1.8" />
      <input
        ref="searchInputRef"
        v-model="searchQuery"
        type="search"
        class="picker-search-input"
        :placeholder="searchPlaceholder" />
      <button
        v-if="searchQuery"
        type="button"
        class="picker-search-clear"
        :title="$t('general.button.close')"
        :aria-label="$t('general.button.close')"
        @click="clearSearch">
        <IconX class="h-4 w-4" stroke="1.8" />
      </button>
    </div>

    <div class="picker-usage-list" role="tablist" aria-orientation="vertical">
      <button
        v-for="group in groups"
        :key="group.type"
        type="button"
        class="picker-usage-button"
        role="tab"
        :aria-selected="isUsagePaneActive(group.type) ? 'true' : 'false'"
        :class="{ 'is-active': isUsagePaneActive(group.type) }"
        @click="activateUsage(group.type)">
        <span class="picker-usage-label">{{ group.name }}</span>
      </button>
    </div>

    <div class="picker-detail">
      <div ref="pickerListRef" class="picker-list">
        <button
          v-if="pinnedSelectedBuildingType"
          type="button"
          class="picker-option picker-option-child picker-option-pinned"
          :class="{
            'is-active': isBuildingTypeSelected(pinnedSelectedBuildingType.usageType, pinnedSelectedBuildingType.id),
          }"
          @click="
            selectBuildingType(
              pinnedSelectedBuildingType.usageType,
              pinnedSelectedBuildingType.id,
              pinnedSelectedBuildingType.name
            )
          ">
          <div class="min-w-0">
            <div class="truncate font-medium">{{ pinnedSelectedBuildingType.name }}</div>
            <div class="picker-option-subtitle truncate">{{ pinnedSelectedBuildingType.usageName }}</div>
          </div>
        </button>

        <button
          v-for="group in usageOnlyOptions"
          :key="`usage-only-${group.type}`"
          type="button"
          class="picker-option picker-option-parent"
          :class="{ 'is-active': isUsageOnlySelected(group.type) }"
          @click="selectUsageOnly(group.type)">
          <div class="min-w-0">
            <div class="truncate font-medium">{{ group.name }}</div>
            <div class="picker-option-subtitle truncate">
              {{ wt('usages', 'picker', 'noSpecificBuildingType') }}
            </div>
          </div>
        </button>

        <template v-if="orderedBuildingTypes.length">
          <button
            v-for="buildingType in orderedBuildingTypes"
            :key="buildingType.id"
            type="button"
            class="picker-option picker-option-child"
            :class="{ 'is-active': isBuildingTypeSelected(buildingType.usageType, buildingType.id) }"
            @click="selectBuildingType(buildingType.usageType, buildingType.id, buildingType.name)">
            <div class="min-w-0">
              <div class="truncate font-medium">{{ buildingType.name }}</div>
              <div class="picker-option-subtitle truncate">{{ buildingType.usageName }}</div>
            </div>
          </button>
        </template>

        <div v-else-if="showEmptyState" class="picker-empty">
          {{ wt('usages', 'picker', 'empty') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { IconSearch, IconX } from '@tabler/icons-vue'
import { searchBuildingTypes } from '@/helpers/building-type-search'
import { useWidgetText } from '@/helpers/useWidgetText'
import { useContentStore } from '@/stores/content'
import { STANDARD_USAGE_TYPES } from '@/helpers/usage-types'

type PickerSelection = {
  usageType: string
  buildingTypeId: string | null
  buildingTypeName: string | null
}

const props = withDefaults(
  defineProps<{
    country?: string | null
    selectedUsageType?: string | null
    selectedBuildingTypeId?: string | null
    usageCounts?: Record<string, number>
    allowedUsageTypes?: readonly string[]
  }>(),
  {
    country: null,
    selectedUsageType: null,
    selectedBuildingTypeId: null,
    usageCounts: () => ({}),
    allowedUsageTypes: () => STANDARD_USAGE_TYPES,
  }
)

const emit = defineEmits<{
  (e: 'select', value: PickerSelection): void
}>()

const { wt } = useWidgetText('widget-texts-project-general')
const contentStore = useContentStore()
const searchQuery = ref('')
const searchInputRef = ref<HTMLInputElement | null>(null)
const pickerListRef = ref<HTMLElement | null>(null)

type Group = {
  type: string
  name: string
  buildingTypes: Array<{ id: string; name: string }>
}

type BuildingTypeOption = {
  usageType: string
  usageName: string
  id: string
  name: string
}

const activeUsageType = ref<string | null>(props.selectedUsageType ?? null)
function applyUsagePlaceholder(template: string, usageName: string): string {
  const interpolated = template.replace(/\{\{\s*usage\s*\}\}|\{\s*usage\s*\}/gi, usageName)
  if (interpolated !== template) return interpolated
  return `${template} (${usageName})`
}

const searchPlaceholder = computed(() => {
  if (!activeUsageType.value) {
    return wt('usages', 'picker', 'searchPlaceholderGlobal') || wt('usages', 'picker', 'searchPlaceholder')
  }

  const usageName = wt('usages', 'option', activeUsageType.value)
  const scopedPlaceholder = wt('usages', 'picker', 'searchPlaceholderWithinUsage')
  if (scopedPlaceholder) {
    return usageName ? applyUsagePlaceholder(scopedPlaceholder, usageName) : scopedPlaceholder
  }

  const fallbackBase = wt('usages', 'picker', 'searchPlaceholder')
  return usageName && fallbackBase ? `${fallbackBase} (${usageName})` : fallbackBase
})

const groups = computed<Group[]>(() => {
  const allBuildingTypes = contentStore.getBuildingTypes

  return props.allowedUsageTypes.map((usageType) => {
    const buildingTypes = allBuildingTypes
      .filter((item) => item.usageType === usageType)
      .sort((a, b) => a.name.localeCompare(b.name))

    return {
      type: usageType,
      name: wt('usages', 'option', usageType),
      buildingTypes: buildingTypes.map((item) => ({ id: item.id, name: item.name })),
    }
  })
})

/* The picker is sized for 6 usage rows by default; with both landscape roles selected the
   list holds 7 usages, so the layout grows with the row count instead of squeezing rows. */
const pickerRowCount = computed(() => Math.max(groups.value.length, 6))

const scopedGroups = computed<Group[]>(() => {
  if (!activeUsageType.value) return groups.value
  return groups.value.filter((group) => group.type === activeUsageType.value)
})

// VAL-159: the generic "no specific building type" option is no longer offered.
// It only stays visible while it is the current selection of an existing usage
// (so old projects remain readable) or when the usage has no building types at all.
// While the building types are still loading, an empty list does not count as "none".
const usageOnlyOptions = computed<Group[]>(() => {
  if (!activeUsageType.value) return []
  return scopedGroups.value.filter(
    (group) => isUsageOnlySelected(group.type) || (contentStore.buildingTypesLoaded && group.buildingTypes.length === 0)
  )
})

const filteredBuildingTypes = computed<BuildingTypeOption[]>(() => {
  const buildingTypes = scopedGroups.value.flatMap((group) =>
    group.buildingTypes.map((buildingType) => ({
      usageType: group.type,
      usageName: group.name,
      id: buildingType.id,
      name: buildingType.name,
    }))
  )
  return searchBuildingTypes(buildingTypes, searchQuery.value)
})

const pinnedSelectedBuildingType = computed<BuildingTypeOption | null>(() => {
  if (!props.selectedUsageType || !props.selectedBuildingTypeId) return null

  return (
    filteredBuildingTypes.value.find(
      (item) => item.usageType === props.selectedUsageType && item.id === props.selectedBuildingTypeId
    ) ?? null
  )
})

const orderedBuildingTypes = computed<BuildingTypeOption[]>(() => {
  if (!pinnedSelectedBuildingType.value) return filteredBuildingTypes.value

  return filteredBuildingTypes.value.filter(
    (item) =>
      !(
        item.usageType === pinnedSelectedBuildingType.value?.usageType &&
        item.id === pinnedSelectedBuildingType.value?.id
      )
  )
})

const showEmptyState = computed(() => !usageOnlyOptions.value.length && !filteredBuildingTypes.value.length)

watch(
  () => props.selectedUsageType,
  (selectedUsageType) => {
    activeUsageType.value = selectedUsageType ?? null
  }
)

watch(
  groups,
  (nextGroups) => {
    if (!nextGroups.length) return
    if (activeUsageType.value && !nextGroups.some((group) => group.type === activeUsageType.value)) {
      activeUsageType.value = null
    }
  },
  { immediate: true }
)

function isUsageOnlySelected(type: string): boolean {
  return props.selectedUsageType === type && !props.selectedBuildingTypeId
}

function isBuildingTypeSelected(type: string, buildingTypeId: string): boolean {
  return props.selectedUsageType === type && props.selectedBuildingTypeId === buildingTypeId
}

function isUsagePaneActive(type: string): boolean {
  return activeUsageType.value === type
}

function activateUsage(usageType: string): void {
  activeUsageType.value = activeUsageType.value === usageType ? null : usageType
  if (pickerListRef.value) {
    pickerListRef.value.scrollTop = 0
  }
  void nextTick(() => {
    searchInputRef.value?.focus()
  })
}

function clearSearch(): void {
  searchQuery.value = ''
  searchInputRef.value?.focus()
}

function selectUsageOnly(usageType: string): void {
  emit('select', { usageType, buildingTypeId: null, buildingTypeName: null })
}

function selectBuildingType(usageType: string, buildingTypeId: string, buildingTypeName: string): void {
  emit('select', { usageType, buildingTypeId, buildingTypeName })
}

onMounted(async () => {
  await nextTick()
  searchInputRef.value?.focus()
})
</script>

<style scoped lang="postcss">
.usage-hierarchy-picker {
  --picker-row-height: 2.875rem;
  --picker-row-gap: 0.5rem;
  --picker-row-count: 6;
  @apply grid gap-2;
  grid-template-columns: max-content minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  height: calc(
    (var(--picker-row-height) * var(--picker-row-count)) + (var(--picker-row-gap) * var(--picker-row-count)) + 2.5rem
  );
}

.picker-search-full {
  grid-column: 1 / -1;
}

.picker-usage-list {
  @apply grid;
  gap: var(--picker-row-gap);
  grid-auto-rows: minmax(0, 1fr);
  grid-template-rows: repeat(var(--picker-row-count), minmax(0, 1fr));
  grid-column: 1;
  grid-row: 2;
}

.picker-usage-button {
  @apply flex min-w-0 items-center justify-between gap-2 border border-neutral-200 bg-white px-3 text-left text-neutral-750;
  @apply transition-colors hover:border-neutral-300;
  min-height: var(--picker-row-height);
}

.picker-usage-button:hover {
  background-color: #f5f5f5;
}

.picker-usage-label {
  @apply whitespace-nowrap font-medium;
}

.picker-usage-button.is-active {
  @apply text-primary-dark;
  border-color: color-mix(in srgb, var(--color-primary-default) 48%, white);
  background-color: color-mix(in srgb, var(--color-primary-default) 3%, white);
}

.picker-detail {
  @apply flex min-h-0 flex-col gap-2;
  grid-column: 2;
  grid-row: 2;
}

.picker-search {
  @apply flex items-center gap-2 border border-neutral-300 bg-white px-3 py-2;
}

.picker-search-input {
  @apply min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-neutral-750 outline-none;
}

.picker-search-input::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
  display: none;
}

.picker-search-input::-ms-clear {
  display: none;
}

.picker-search-clear {
  @apply flex h-5 w-5 items-center justify-center text-neutral-500 transition-colors;
}

.picker-search-clear:hover {
  @apply text-primary-dark;
}

.picker-list {
  @apply min-h-0 flex-1 overflow-y-auto overflow-x-hidden border border-neutral-200 bg-white p-2;
}

.picker-option {
  @apply flex w-full items-center justify-between gap-3 rounded-none px-3 py-2 text-left;
  @apply border border-transparent;
  @apply text-neutral-750;
}

.picker-option:hover {
  background-color: #f5f5f5;
}

.picker-option.is-active {
  @apply rounded-none text-primary-dark;
  border-color: color-mix(in srgb, var(--color-primary-default) 48%, white);
  background-color: color-mix(in srgb, var(--color-primary-default) 3%, white);
}

.picker-option.is-active .picker-option-subtitle {
  @apply text-primary-dark;
}

.picker-option-parent {
  @apply mb-2 rounded-none border-dashed border-neutral-300 bg-neutral-25;
}

.picker-option-parent:not(.is-active):hover {
  @apply border-neutral-300;
  background-color: #f5f5f5;
}

.picker-option-child {
  @apply ml-0 pl-3;
}

.picker-option-pinned {
  @apply mb-2;
}

.picker-option-subtitle {
  @apply text-xs text-neutral-550;
}

.picker-empty {
  @apply px-2 py-3 text-sm text-neutral-550;
}
</style>
